import { URL_REGEX } from './constants';

/**
 * Parsed URL components
 */
export interface ParsedUrl {
  /** The protocol/scheme (e.g., 'http', 'https') */
  protocol: string;
  /** The hostname (e.g., 'example.com') */
  hostname: string;
  /** The port number (e.g., '8080') or empty string */
  port: string;
  /** The full path (e.g., '/path/to/resource') */
  pathname: string;
  /** The query string without '?' (e.g., 'foo=bar&baz=qux') */
  search: string;
  /** The hash/fragment without '#' (e.g., 'section') */
  hash: string;
  /** The userinfo (user:password) if present */
  userinfo: string;
  /** The full authority (userinfo@host:port) */
  authority: string;
  /** The full host (hostname:port) */
  host: string;
  /** The original URL string */
  href: string;
}

/**
 * Parses a URL string into its component parts using RFC 3986 regex.
 *
 * This implementation provides Safari < 18 compatibility without requiring
 * the URL API or polyfills.
 *
 * @param url - The URL string to parse
 * @returns Parsed URL components
 *
 * @example
 * ```typescript
 * const parsed = parseUrl('https://user:pass@example.com:8080/path?query=value#hash');
 * // {
 * //   protocol: 'https',
 * //   hostname: 'example.com',
 * //   port: '8080',
 * //   pathname: '/path',
 * //   search: 'query=value',
 * //   hash: 'hash',
 * //   userinfo: 'user:pass',
 * //   authority: 'user:pass@example.com:8080',
 * //   host: 'example.com:8080',
 * //   href: 'https://user:pass@example.com:8080/path?query=value#hash'
 * // }
 * ```
 */
export function parseUrl(url: string): ParsedUrl {
  const match = url.match(URL_REGEX);

  if (!match) {
    throw new Error(`Invalid URL: ${url}`);
  }

  const [, scheme, authority, userinfo, hostname, port, pathname, search, hash] = match;

  return {
    protocol: scheme || '',
    hostname: hostname || '',
    port: port || '',
    pathname: pathname || '',
    search: search || '',
    hash: hash || '',
    userinfo: userinfo ? userinfo.replace('@', '') : '',
    authority: authority || '',
    host: hostname && port ? `${hostname}:${port}` : hostname || '',
    href: url,
  };
}

/**
 * Checks if a URL is absolute (has a protocol).
 *
 * @param url - The URL string to check
 * @returns True if the URL is absolute
 *
 * @example
 * ```typescript
 * isAbsoluteUrl('https://example.com'); // true
 * isAbsoluteUrl('/path/to/page'); // false
 * isAbsoluteUrl('//example.com'); // false
 * ```
 */
export function isAbsoluteUrl(url: string): boolean {
  const parsed = parseUrl(url);

  return !!parsed.protocol;
}

/**
 * Checks if a URL is a protocol-relative URL (starts with //).
 *
 * @param url - The URL string to check
 * @returns True if the URL is protocol-relative
 *
 * @example
 * ```typescript
 * isProtocolRelativeUrl('//example.com/path'); // true
 * isProtocolRelativeUrl('https://example.com'); // false
 * isProtocolRelativeUrl('/path'); // false
 * ```
 */
export function isProtocolRelativeUrl(url: string): boolean {
  return url.startsWith('//');
}

/**
 * Checks if a URL is a path-only URL (relative).
 *
 * @param url - The URL string to check
 * @returns True if the URL is path-only
 *
 * @example
 * ```typescript
 * isPathOnlyUrl('/path/to/page'); // true
 * isPathOnlyUrl('https://example.com'); // false
 * isPathOnlyUrl('//example.com'); // false
 * ```
 */
export function isPathOnlyUrl(url: string): boolean {
  const parsed = parseUrl(url);

  return !parsed.protocol && !parsed.hostname;
}
